import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

type Coords = { top: number; left: number };

interface PortalTooltipProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  children: React.ReactNode;
  zIndex?: number;
}

/**
 * Lightweight portal-based tooltip anchor.
 * Positions a fixed container at the anchor's center; render your tooltip inside.
 */
export const PortalTooltip: React.FC<PortalTooltipProps> = ({
  anchorEl,
  open,
  children,
  zIndex = 10000,
}) => {
  const [coords, setCoords] = useState<Coords | null>(null);

  const updatePosition = () => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setCoords({ top: rect.top, left: rect.left + rect.width / 2 });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    // Reposition on scroll/resize (capture to catch nested scrolls)
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, anchorEl]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: "translate(-50%, 0)",
        zIndex,
        pointerEvents: "none",
      }}
    >
      {children}
    </div>,
    document.body
  );
};
